import { AxiosResponse } from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getData } from "../../API/index";
import { Permiso } from "../../API/interfaces";
import "../../assets/scss/components/_permissions-component.scss";
import Button from "../../components/Button";
import { useAuth } from "../../context/AuthContext";

interface PermissionsComponentProps {
  permisosSeleccionados: string[]; // IDs de los permisos seleccionados
  onChangeCheckbox?: (value: string, isChecked: boolean) => void;
  onToggleAllCheckbox: (newPermisos: string[]) => void;
}

const PermissionsComponent: React.FC<PermissionsComponentProps> = ({
  permisosSeleccionados,
  onChangeCheckbox,
  onToggleAllCheckbox,
}) => {
  const navigate = useNavigate();
  const urlGetDatos = "/api/permisos";
  const { token, refreshAccessToken } = useAuth();
  const [permissions, setpermissions] = useState<Permiso[]>([]);
  const [checkboxes, setCheckboxes] = useState<Record<string, boolean>>({});

  const handleCheckboxChange = (id: string) => {
    setCheckboxes((prevCheckboxes) => ({
      ...prevCheckboxes,
      [id]: !prevCheckboxes[id],
    }));

    if (onChangeCheckbox) {
      onChangeCheckbox(id, !checkboxes[id]);
    }
  };

  const handleToggleAll = () => {
    const isAllChecked =
      Object.values(checkboxes).length > 0 &&
      Object.values(checkboxes).every((isChecked) => isChecked);

    setCheckboxes(() => {
      const updatedCheckboxes: Record<string, boolean> = {};

      permissions.forEach(({ id }) => {
        updatedCheckboxes[id] = !isAllChecked;
      });

      onToggleAllCheckbox(isAllChecked ? [] : Object.keys(updatedCheckboxes));
      return updatedCheckboxes;
    });
  };

  const areAllChecked =
    Object.values(checkboxes).length > 0 &&
    Object.values(checkboxes).every((isChecked) => isChecked);

  const buttonText = areAllChecked ? "Deseleccionar todo" : "Seleccionar todo";

  const groupedData = permissions.reduce((acc, permission) => {
    const { seccion, subseccion } = permission;
    if (!acc[seccion]) {
      acc[seccion] = {};
    }
    if (!acc[seccion][subseccion]) {
      acc[seccion][subseccion] = {};
    }
    acc[seccion][subseccion][permission.id] =
      checkboxes[permission.id] || false;
    return acc;
  }, {} as Record<string, Record<string, Record<string, boolean>>>);

  // Llamada a la API para recibir los datos
  const fetchData = async () => {
    try {
      const response: AxiosResponse = await getData(
        `${urlGetDatos}`,
        navigate,
        token,
        refreshAccessToken
      );
      if (response && response.status === 200) {
        const transformedData = response.data.permisos;
        setpermissions(transformedData);

        // Inicializar checkboxes con permisosSeleccionados
        setCheckboxesSelected();
      }
    } catch (error) {
      console.log("Error al obtener datos desde la API");
    }
  };

  const setCheckboxesSelected = () => {
    const initialCheckboxes: Record<string, boolean> = {};
    if (permisosSeleccionados.length > 0) {
      permisosSeleccionados.forEach((id: string) => {
        initialCheckboxes[id] = true;
      });

      setCheckboxes(initialCheckboxes);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setCheckboxesSelected();
  }, [permisosSeleccionados]);

  return (
    <div className="permissions-container">
      <div>
        <h2>Accesos y permisos</h2>

        <Button color="white" onClick={handleToggleAll}>
          {buttonText}
        </Button>
      </div>
      <div className="elements-container">
        <div className="columns-container">
          <p style={{ position: "absolute" }}>Sección</p>
          <p style={{ marginLeft: "150px" }}>Subsección</p>
          <p>Funciones</p>
        </div>

        {Object.entries(groupedData).map(([seccion, subseccions]) => (
          <div key={seccion}>
            <p style={{ position: "absolute" }}>{seccion.toUpperCase()}</p>
            <div style={{ marginLeft: "150px" }}>
              {Object.entries(subseccions).map(([subseccionKey, items]) => (
                <div key={subseccionKey}>
                  <h3>{subseccionKey}</h3>
                  {Object.entries(items).map(([id, isChecked]) => (
                    <div key={id}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        value={
                          permissions.find(
                            (permission) => String(permission.id) === id
                          )?.id || ""
                        }
                        onChange={() => handleCheckboxChange(id)}
                      />
                      <label>
                        {permissions.find(
                          (permission) => String(permission.id) === id
                        )?.label || ""}
                      </label>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PermissionsComponent;
